/**
 * PKCE S256 — a prova de posse do fluxo de codigo de autorizacao.
 *
 * docs/AUTENTICACAO-E-ACESSO.md, secao 2, passo 2: a aplicacao inicia o fluxo
 * de codigo de autorizacao com PKCE S256. O cliente `teg-web` e publico
 * (infra/keycloak/realm-teg.json) e o Keycloak exige S256 —
 * `pkce.code.challenge.method: S256`. Sem o verificador correto na troca, o
 * codigo interceptado nao vale nada.
 *
 * O verificador NAO e token: e um valor de uso unico, valido por minutos, que
 * so serve para provar que quem troca o codigo e quem pediu o codigo. Por isso
 * ele pode sobreviver ao redirecionamento em `sessionStorage`
 * (ver deposito-pedido.ts). Token de acesso, de renovacao e de identidade,
 * esses, nunca saem da memoria.
 */

/** Tamanho em bytes do verificador. 32 bytes viram 43 caracteres em base64url. */
const BYTES_VERIFICADOR = 32;

export interface DesafioPkce {
  verificador: string;
  desafio: string;
  metodo: 'S256';
}

/**
 * Codifica em base64url sem preenchimento, como o RFC 7636 exige.
 *
 * `btoa` trabalha com string de bytes, entao a conversao passa por
 * `String.fromCharCode` — `TextDecoder` interpretaria os bytes como texto e
 * corromperia o valor.
 */
export function paraBase64Url(bytes: Uint8Array): string {
  let bruto = '';
  for (const byte of bytes) {
    bruto += String.fromCharCode(byte);
  }
  return btoa(bruto).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Bytes aleatorios de qualidade criptografica. */
function aleatorio(quantidade: number): Uint8Array {
  const bytes = new Uint8Array(quantidade);
  crypto.getRandomValues(bytes);
  return bytes;
}

/**
 * Valor opaco de uso unico, para `state` e para o verificador.
 *
 * `state` amarra a resposta do Keycloak ao pedido que esta aplicacao fez: sem
 * conferir isso, uma resposta plantada por outra pagina seria aceita.
 */
export function gerarValorOpaco(bytes = BYTES_VERIFICADOR): string {
  return paraBase64Url(aleatorio(bytes));
}

/** SHA-256 do verificador, em base64url — o `code_challenge` do S256. */
export async function calcularDesafio(verificador: string): Promise<string> {
  const digerido = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verificador));
  return paraBase64Url(new Uint8Array(digerido));
}

/** Par verificador/desafio para um novo pedido de autorizacao. */
export async function gerarDesafioPkce(): Promise<DesafioPkce> {
  const verificador = gerarValorOpaco();
  return { verificador, desafio: await calcularDesafio(verificador), metodo: 'S256' };
}
